import { Icon } from "@iconify/react";
import { Box, IconButton, Stack, SxProps, Theme, Typography } from "@mui/material";
import { UploadSource } from "@rms-types/lib/s3/interface";
import { AttachmentMeta } from "@rms-types/modules/enrollment/types";
import { Fragment, ReactNode } from "react";
import { DropzoneProps } from "react-dropzone";
import { Controller, FieldValues, Path, useFormContext } from "react-hook-form";

import { DropArea, TrackingFilesList } from "@/components";
import { ContentBox } from "@/components/elements/content-box";
import { AttachmentDisplay } from "@/features";
import { useFileUpload } from "@/hooks/files/useFileUpload";
import { useFileUploadState } from "@/hooks/files/useFileUploadState";

export type FormPublicUploaderValue = Array<AttachmentMeta>;

export interface HandleUploadFileCallback {
  payload: AttachmentMeta;
  name: string;
}

export interface HandleRemoveFileCallback {
  index: number;
  name: string;
}

export type FormPublicUploaderProps<TFormValues extends FieldValues> = DropzoneProps & {
  name: Path<TFormValues>;
  uploadSource: UploadSource;
  title?: string;
  titleSx?: SxProps<Theme>;
  subtitle?: string | ReactNode;
  maxFiles?: number;
};

export function FormPublicUploader<TFormValues extends FieldValues>({
  name,
  title,
  subtitle,
  uploadSource,
  maxFiles = 5,
  titleSx = {},
  ...rest
}: FormPublicUploaderProps<TFormValues>) {
  const { failedFiles, loadingFiles } = useFileUploadState();
  const { watch, control, getValues, setValue } = useFormContext();
  const { upload } = useFileUpload();
  const oldValues = getValues(name) as unknown as AttachmentMeta[];

  const handleRemoveFileCallback = ({ name, index }: HandleRemoveFileCallback) => {
    const allValues: AttachmentMeta[] = (oldValues ?? []).filter((_, arrIndex) => arrIndex !== index);
    setValue(name, allValues);
  };

  const handleUploadFileCallback = ({ name, payload }: HandleUploadFileCallback) => {
    const allValues: AttachmentMeta[] = [...oldValues, payload];
    setValue(name, allValues);
  };

  const fieldValue = watch(name) as AttachmentMeta[];
  const attachmentsLink = fieldValue ? (Array.isArray(fieldValue) ? fieldValue : [fieldValue]) : [];

  const handleDrop = async (acceptedFiles: File[]) => {
    try {
      const uploadedFiles = await upload(acceptedFiles, uploadSource);

      uploadedFiles.forEach((file) =>
        handleUploadFileCallback({ name: name as HandleUploadFileCallback["name"], payload: file })
      );
    } catch (error) {
      //TODO: Handle error
    }
  };

  return (
    <Stack spacing={2}>
      {title && (
        <Typography variant="h6" sx={titleSx}>
          {title}
        </Typography>
      )}
      {subtitle && <Typography variant="body2">{subtitle}</Typography>}
      <Typography variant="body1">{`Maximum attachments: ${maxFiles}`}</Typography>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <DropArea
            hasError={false}
            disabled={oldValues.length > maxFiles - 1}
            onDrop={handleDrop}
            {...field}
            {...rest}
            ref={null}
          />
        )}
      />
      {/* Tracking Files */}
      <TrackingFilesList loadingFiles={loadingFiles} failedFiles={failedFiles} />

      {/** Fetched files */}
      {attachmentsLink.length > 0 && (
        <ContentBox>
          <Stack py={2} direction="column" spacing={2}>
            {attachmentsLink.map(({ name: fileName }, index) => {
              return (
                <Fragment key={fileName + index}>
                  <Stack px={2} direction="row" alignItems="center" spacing={2} justifyContent="space-between">
                    <AttachmentDisplay name={fileName} />

                    <Box display={"flex"}>
                      <IconButton
                        sx={{ marginLeft: "32px", marginRight: "8px" }}
                        color="error"
                        edge="end"
                        onClick={() => {
                          handleRemoveFileCallback({
                            name: name as HandleRemoveFileCallback["name"],
                            index,
                          });
                        }}
                      >
                        <Icon icon="fluent:delete-20-filled" fontSize={23} />
                      </IconButton>
                    </Box>
                  </Stack>
                </Fragment>
              );
            })}
          </Stack>
        </ContentBox>
      )}
    </Stack>
  );
}
